<template>
    <section class="app-agent" :class="{ 'app-agent-conversation': messages.length }" aria-label="GENERIO AGENT">
        <Transition name="agent-fade">
            <v-alert v-if="fileError || requestError" class="app-agent-error" type="error" variant="tonal" density="compact">
                {{ fileError || requestError }}
            </v-alert>
        </Transition>
        <Transition name="agent-view" appear>
        <HeroPrompt
            v-if="!messages.length"
            v-model="draft"
            :attachments="attachments"
            :busy="busy"
            @add-files="addFiles"
            @remove-file="removeFile"
            @submit="submitPrompt" />

        <ConversationDialog
            v-else
            v-model="draft"
            :messages="messages"
            :attachments="attachments"
            :busy="busy"
            :checking="checking"
            :expanded-id="expandedId"
            :opened-message-id="openedMessageId"
            @add-files="addFiles"
            @remove-file="removeFile"
            @submit="submitPrompt"
            @new-conversation="newConversation"
            @retry="retryResponse({ id: $event })"
            @check-status="checkStatus({ id: $event })"
            @expand="setExpanded" />
        </Transition>
    </section>
</template>

<script>
import { mapActions, mapGetters } from '@generio-ai/library-legacy/plugins/vuex'
import HeroPrompt from './hero/HeroPrompt.vue'
import ConversationDialog from './conversation/ConversationDialog.vue'
import './motion.css'

export default {
    name: 'AppAgent',
    components: {
        HeroPrompt,
        ConversationDialog
    },
    data() {
        return {
            draft: '',
            attachments: [],
            fileError: '',
            nextId: 1
        }
    },
    computed: {
        ...mapGetters('agent', {
            messages: 'messages', busy: 'isBusy', checking: 'isChecking',
            requestError: 'error', expandedId: 'expandedId', openedMessageId: 'openedMessageId'
        }),
        ...mapGetters('assets', { synchronized: 'isSynchronized' })
    },
    watch: {
        synchronized(value) { if (value) this.init() }
    },
    methods: {
        ...mapActions('agent', {
            init: 'init', sendPrompt: 'submitPrompt', resetConversation: 'newConversation',
            retryResponse: 'retryResponse', checkStatus: 'checkStatus', setExpanded: 'setExpanded'
        }),
        addFiles(files) {
            if (this.busy) return
            this.fileError = ''
            if (files.some(file => !/^image\/(png|jpeg|webp|bmp|tiff)$/.test(file.type) && !/\.(png|jpe?g|webp|bmp|tiff?)$/i.test(file.name))) {
                this.fileError = 'Please choose PNG, JPEG, WebP, BMP, or TIFF reference images.'
                return
            }
            this.attachments.push(...files.map(file => ({
                id: this.nextId++,
                name: file.name,
                file,
                preview: URL.createObjectURL(file)
            })))
        },
        removeFile(id) {
            if (this.busy) return
            const attachment = this.attachments.find(file => file.id === id)
            if (attachment?.preview) URL.revokeObjectURL(attachment.preview)
            this.attachments = this.attachments.filter(file => file.id !== id)
        },
        async submitPrompt() {
            const text = this.draft.trim()
            if (!text || this.busy) return
            const accepted = await this.sendPrompt({ text, attachments: this.attachments })
            if (!accepted) return
            this.releasePreviews()
            this.draft = ''
            this.attachments = []
            this.fileError = ''
        },
        releasePreviews() {
            this.attachments.forEach(file => {
                if (file.preview) URL.revokeObjectURL(file.preview)
            })
        },
        async newConversation() {
            if (!await this.resetConversation()) return
            this.releasePreviews()
            this.draft = ''
            this.attachments = []
            this.fileError = ''
        }
    },
    mounted() {
        this.init()
    },
    beforeUnmount() {
        this.releasePreviews()
    }
}
</script>

<style scoped>
.app-agent {
    position: relative;
    width: 100%;
    min-height: calc(100dvh - var(--v-layout-top, 0px) - var(--v-layout-bottom, 0px));
    display: flex;
    flex-direction: column;
    color: rgb(var(--v-theme-primary));
    background: rgb(var(--v-theme-darkbase));
}

.app-agent-error {
    flex: none;
    margin: 8px 16px;
}

.app-agent-conversation {
    height: calc(100dvh - var(--v-layout-top, 0px) - var(--v-layout-bottom, 0px));
    min-height: 0;
    overflow: hidden;
}
</style>
